// frontend/src/pages/InvoiceEntryPage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { 
    UploadCloud, FileText, CheckCircle, AlertTriangle, 
    Search, Save, Package, Loader2, Warehouse, ChevronLeft, ChevronRight, Check, Trash2, X, Eye, Printer, PlusCircle
} from 'lucide-react';

const ITEMS_PER_PAGE = 12;

// ============================================================================
// COMPONENTES AUXILIARES PARA O MODAL FINANCEIRO E FORNECEDOR
// ============================================================================
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const FornecedorSearchInput = ({ onFornecedorSelect, initialFornecedor = null }) => {
    const { apiFetch } = useApi();
    const [searchTerm, setSearchTerm] = useState(initialFornecedor ? initialFornecedor.name : '');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState(initialFornecedor);
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (initialFornecedor) {
            setSearchTerm(initialFornecedor.name);
            setSelected(initialFornecedor);
        }
    }, [initialFornecedor]);

    useEffect(() => {
        if (debouncedSearchTerm && !selected && !initialFornecedor) {
            setLoading(true);
            apiFetch(`/contas-a-pagar/fornecedores?search=${debouncedSearchTerm}`)
                .then(data => setResults(data))
                .catch(err => console.error(err))
                .finally(() => setLoading(false));
        } else {
            setResults([]);
        }
    }, [debouncedSearchTerm, apiFetch, selected, initialFornecedor]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setResults([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelect = (fornecedor) => {
        setSearchTerm(fornecedor.name);
        setSelected(fornecedor);
        onFornecedorSelect(fornecedor);
        setResults([]);
    };

    const handleInputChange = (e) => {
        setSearchTerm(e.target.value);
        setSelected(null); 
        onFornecedorSelect(null); 
    };

    return (
        <div className="relative" ref={wrapperRef}>
            <input
                type="text"
                value={searchTerm}
                onChange={handleInputChange}
                placeholder="Pesquisar fornecedor (Nome, CPF/CNPJ...)"
                className="w-full p-2 mt-1 border border-gray-300 rounded-md focus:ring-blue-500 text-sm shadow-sm"
                required
                disabled={!!initialFornecedor} // Bloqueia se vier preenchido
            />
            {loading && <Loader2 size={16} className="animate-spin absolute right-3 top-3.5 text-gray-400" />}
            {results.length > 0 && !initialFornecedor && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {results.map(f => (
                        <li 
                            key={f.id} 
                            onClick={() => handleSelect(f)}
                            className="p-3 hover:bg-gray-100 cursor-pointer"
                        >
                            <p className="font-medium">{f.name}</p>
                            <p className="text-sm text-gray-500">{f.document_id || `Cód: ${f.client_number}`}</p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ============================================================================
// NOVO COMPONENTE: BUSCA INTELIGENTE DE PRODUTOS PARA ENTRADA MANUAL
// ============================================================================
const ProdutoFornecedorSearchInput = ({ catalog, onProductSelect, selectedVariationId }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (selectedVariationId && catalog) {
            const found = catalog.find(p => String(p.variation_id) === String(selectedVariationId));
            if (found) {
                setSearchTerm(found.product_name);
                setSelected(found);
            }
        } else if (!selectedVariationId) {
            setSearchTerm('');
            setSelected(null);
        }
    }, [selectedVariationId, catalog]);

    useEffect(() => {
        if (searchTerm && !selected && catalog) {
            const term = searchTerm.toLowerCase();
            const filtered = catalog.filter(p => 
                (p.product_name && p.product_name.toLowerCase().includes(term)) ||
                (p.ean && p.ean.toLowerCase().includes(term)) ||
                (p.code && p.code.toLowerCase().includes(term))
            ).slice(0, 10); // Mostra até 10 resultados para não poluir a tela
            setResults(filtered);
        } else {
            setResults([]);
        }
    }, [searchTerm, catalog, selected]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setResults([]);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    const handleSelect = (product) => {
        setSearchTerm(product.product_name);
        setSelected(product);
        onProductSelect(product);
        setResults([]);
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setSelected(null);
                    }}
                    placeholder="Buscar (Nome, EAN, Cód)"
                    className={`w-full pl-8 p-2 border rounded-md text-sm shadow-sm focus:ring-2 focus:ring-blue-500 ${selected ? 'border-gray-300 bg-white' : 'border-red-300 bg-red-50'}`}
                />
            </div>
            {results.length > 0 && (
                <ul className="absolute z-50 w-[150%] sm:w-full min-w-[250px] mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto left-0">
                    {results.map(p => (
                        <li 
                            key={p.variation_id} 
                            onClick={() => handleSelect(p)}
                            className="p-3 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                        >
                            <p className="font-bold text-sm text-gray-800 truncate">{p.product_name}</p>
                            <p className="text-xs text-gray-500 flex gap-2">
                                {p.ean && <span>EAN: {p.ean}</span>}
                                {p.code && <span>COD: {p.code}</span>}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// ============================================================================
// MODAL DE CONTAS A PAGAR
// ============================================================================
const NovaFaturaModal = ({ onClose, onSave, formatCurrency, initialData }) => {
    const { apiFetch } = useApi();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // Inicia com os dados fornecidos
    const [selectedFornecedor, setSelectedFornecedor] = useState(initialData?.fornecedor || null);
    const [numeroNF, setNumeroNF] = useState(initialData?.nf_numero || '');
    const [dataEmissao, setDataEmissao] = useState(initialData?.data_emissao || new Date().toISOString().split('T')[0]);
    const [valorTotalFatura, setValorTotalFatura] = useState(initialData?.valor_total || 0);
    const [observacoes, setObservacoes] = useState('');

    const [numeroParcelas, setNumeroParcelas] = useState(1);
    const [meioPagamento, setMeioPagamento] = useState('pix'); 
    const [parcelas, setParcelas] = useState([]);

    useEffect(() => {
        const total = parcelas.reduce((acc, p) => acc + (parseFloat(p.valor_parcela) || 0), 0);
        setValorTotalFatura(total);
    }, [parcelas]);

    useEffect(() => {
        const dataBase = dataEmissao || new Date().toISOString().split('T')[0];
        
        const valorPorParcela = initialData?.valor_total ? (initialData.valor_total / numeroParcelas).toFixed(2) : 0;

        setParcelas(Array.from({ length: numeroParcelas }, (_, i) => {
            const vencimento = new Date(dataBase);
            vencimento.setMonth(vencimento.getMonth() + (i + 1));
            
            let valorCalculado = parseFloat(valorPorParcela);
            if (initialData?.valor_total && i === numeroParcelas - 1) {
                const somaAnteriores = (valorCalculado * (numeroParcelas - 1));
                valorCalculado = (initialData.valor_total - somaAnteriores).toFixed(2);
            }

            return {
                id: i,
                numero_parcela: i + 1,
                data_vencimento: vencimento.toISOString().split('T')[0],
                valor_parcela: valorCalculado,
                pix_chave: meioPagamento === 'pix' ? '' : null,
                boleto_numero: meioPagamento === 'boleto' ? '' : null,
                boleto_cod_barras: meioPagamento === 'boleto' ? '' : null,
            };
        }));
    }, [numeroParcelas, meioPagamento, dataEmissao, initialData]);

    const handleParcelaChange = (index, field, value) => {
        const novasParcelas = [...parcelas];
        novasParcelas[index] = { ...novasParcelas[index], [field]: value };
        setParcelas(novasParcelas);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        if (!selectedFornecedor) {
            setError('Por favor, pesquise e selecione um fornecedor.');
            setLoading(false);
            return;
        }

        const faturaData = {
            fatura: {
                fornecedor_id: parseInt(selectedFornecedor.id, 10),
                nf_numero: numeroNF || null,
                data_emissao: dataEmissao,
                valor_total: valorTotalFatura,
                observacoes: observacoes || null,
            },
            parcelas: parcelas.map(p => ({
                numero_parcela: p.numero_parcela,
                data_vencimento: p.data_vencimento,
                valor_parcela: parseFloat(p.valor_parcela) || 0,
                meio_pagamento: meioPagamento,
                pix_chave: p.pix_chave || null,
                boleto_numero: p.boleto_numero || null,
                boleto_cod_barras: p.boleto_cod_barras || null,
            }))
        };

        try {
            await apiFetch('/contas-a-pagar', {
                method: 'POST',
                body: faturaData,
            });
            onSave();
        } catch (err) {
            console.error("Erro ao salvar fatura:", err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold text-gray-800">Lançar Nova Fatura a Pagar</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-200"><X size={20} /></button>
                </div>
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                    <div className="p-6 space-y-6">
                        {error && <p className="p-3 text-sm text-red-700 bg-red-100 rounded-md">{error}</p>}
                        
                        {/* AVISO DE AUTO PREENCHIMENTO */}
                        {initialData && (
                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-md">
                                <p className="text-sm text-blue-700">
                                    <strong>Praticidade:</strong> Alguns campos já foram preenchidos com os dados da Nota Fiscal recém processada.
                                </p>
                            </div>
                        )}

                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Dados da Fatura</h3>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Fornecedor*</label>
                                    <FornecedorSearchInput 
                                        onFornecedorSelect={setSelectedFornecedor}
                                        initialFornecedor={selectedFornecedor}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº da NF</label>
                                    <input type="text" value={numeroNF} onChange={e => setNumeroNF(e.target.value)} className="w-full p-2 mt-1 border rounded-md bg-gray-50" disabled={!!initialData} />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Data de Emissão*</label>
                                    <input type="date" value={dataEmissao} onChange={e => setDataEmissao(e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nº de Parcelas*</label>
                                    <select value={numeroParcelas} onChange={e => setNumeroParcelas(Number(e.target.value))} className="w-full p-2 mt-1 border rounded-md">
                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => <option key={n} value={n}>{n}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Meio de Pagamento*</label>
                                    <select value={meioPagamento} onChange={e => setMeioPagamento(e.target.value)} className="w-full p-2 mt-1 border rounded-md">
                                        <option value="pix">PIX</option>
                                        <option value="boleto">Boleto</option>
                                        <option value="dinheiro">Dinheiro</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Valor Total da Fatura</label>
                                    <p className="w-full p-2 mt-1 font-bold text-gray-800">
                                        {formatCurrency(valorTotalFatura, 'BRL')}
                                    </p>
                                </div>
                                <div className="md:col-span-3">
                                    <label className="block text-sm font-medium text-gray-700">Observações</label>
                                    <textarea 
                                        value={observacoes} 
                                        onChange={e => setObservacoes(e.target.value)} 
                                        className="w-full p-2 mt-1 border rounded-md"
                                        rows="2"
                                        placeholder="Detalhes adicionais sobre a fatura..."
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 border rounded-lg">
                            <h3 className="mb-4 text-lg font-semibold">Parcelas</h3>
                            <div className="space-y-4">
                                {parcelas.map((parcela, index) => (
                                    <div key={parcela.id} className="p-3 bg-gray-50 rounded-md">
                                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Parcela</label>
                                                <input type="text" value={parcela.numero_parcela} disabled className="w-full p-2 mt-1 bg-gray-200 border rounded-md" />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Vencimento*</label>
                                                <input type="date" value={parcela.data_vencimento} onChange={e => handleParcelaChange(index, 'data_vencimento', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                            </div>
                                            <div>
                                                <label className="text-xs font-medium text-gray-600">Valor*</label>
                                                <input type="number" step="0.01" value={parcela.valor_parcela} onChange={e => handleParcelaChange(index, 'valor_parcela', e.target.value)} className="w-full p-2 mt-1 border rounded-md" required />
                                            </div>
                                            {meioPagamento === 'pix' && (
                                                <div>
                                                    <label className="text-xs font-medium text-gray-600">Chave PIX</label>
                                                    <input type="text" value={parcela.pix_chave || ''} onChange={e => handleParcelaChange(index, 'pix_chave', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                </div>
                                            )}
                                            {meioPagamento === 'boleto' && (
                                                <>
                                                    <div>
                                                        <label className="text-xs font-medium text-gray-600">Nº do Boleto</label>
                                                        <input type="text" value={parcela.boleto_numero || ''} onChange={e => handleParcelaChange(index, 'boleto_numero', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                    </div>
                                                    <div className="md:col-span-2">
                                                        <label className="text-xs font-medium text-gray-600">Cód. de Barras</label>
                                                        <input type="text" value={parcela.boleto_cod_barras || ''} onChange={e => handleParcelaChange(index, 'boleto_cod_barras', e.target.value)} className="w-full p-2 mt-1 border rounded-md" />
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 p-4 mt-4 bg-gray-50 border-t">
                        <button type="button" onClick={onClose} className="px-4 py-2 font-semibold bg-gray-200 rounded-md">Cancelar</button>
                        <button type="submit" disabled={loading} className="flex items-center justify-center px-4 py-2 font-semibold text-white bg-blue-600 rounded-md disabled:bg-blue-300">
                            {loading ? <Loader2 className="animate-spin" /> : 'Salvar Fatura'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ============================================================================
// MODAL: VISUALIZAR NOTA FISCAL (TELA CHEIA)
// ============================================================================
const VisualizarNotaModal = ({ invoice, onClose, formatCurrency }) => {
    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-gray-100">
            <div className="flex items-center justify-between p-4 border-b bg-white shadow-sm shrink-0">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <FileText className="text-blue-600" /> Detalhes do Lançamento: NF-e nº {invoice.invoice_number}
                </h2>
                <button 
                    onClick={onClose} 
                    className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition-colors"
                >
                    <X size={20} /> Fechar
                </button>
            </div>
            
            <div className="flex-1 p-6 md:p-8 overflow-y-auto w-full">
                <div className="w-full mx-auto space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Fornecedor</p>
                            <p className="font-bold text-lg text-gray-900">{invoice.supplier_name}</p>
                            <p className="text-sm text-gray-600">CNPJ: {invoice.supplier_cnpj || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Documento</p>
                            <p className="font-bold text-lg text-gray-900">NF-e nº {invoice.invoice_number}</p>
                            <p className="text-sm text-gray-600">Série: {invoice.series || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Datas</p>
                            <p className="font-bold text-gray-900">Emissão: {invoice.issue_date ? invoice.issue_date.split('-').reverse().join('/') : '-'}</p>
                            <p className="text-sm text-gray-600">Lançamento: {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                        </div>
                        <div className="md:col-span-2">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Depósito Destino</p>
                            <p className="font-bold text-lg text-gray-900">{invoice.deposit_name || '-'}</p>
                        </div>
                        <div>
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Valor Total</p>
                            <p className="text-3xl font-black text-blue-700">{formatCurrency(invoice.total_amount)}</p>
                        </div>
                    </div>

                    {invoice.observations && (
                        <div className="bg-yellow-50 p-5 rounded-xl border border-yellow-200 shadow-sm">
                            <p className="text-xs font-bold text-yellow-800 uppercase tracking-wider mb-2">Observações Adicionais</p>
                            <p className="text-base text-yellow-900">{invoice.observations}</p>
                        </div>
                    )}

                    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="p-5 border-b bg-gray-50 flex items-center justify-between">
                            <h3 className="font-bold text-lg text-gray-800 flex items-center gap-2">
                                <Package className="text-gray-600" size={20}/> Itens Lançados no Estoque
                            </h3>
                            <span className="text-sm font-bold text-gray-500 bg-gray-200 px-3 py-1 rounded-full">{invoice.items?.length || 0} Itens Registrados</span>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm text-left whitespace-nowrap">
                                <thead className="bg-gray-100 text-gray-600 font-semibold uppercase text-xs tracking-wider border-b">
                                    <tr>
                                        <th className="px-6 py-4">Produto Vinculado (Sistema)</th>
                                        <th className="px-6 py-4">Cód. Fornecedor</th>
                                        <th className="px-6 py-4 text-right">Qtd. na NF</th>
                                        <th className="px-6 py-4 text-center">Fator Conv.</th>
                                        <th className="px-6 py-4 text-right bg-blue-50/50">Qtd. Estoque</th>
                                        <th className="px-6 py-4 text-right">Custo Unitário</th>
                                        <th className="px-6 py-4 text-right bg-gray-50">Custo Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {invoice.items && invoice.items.length > 0 ? (
                                        invoice.items.map((item, index) => (
                                            <tr key={index} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="px-6 py-4 font-medium text-gray-900">{item.product_name}</td>
                                                <td className="px-6 py-4 text-gray-500 text-xs">{item.supplier_product_code}</td>
                                                <td className="px-6 py-4 text-right text-gray-600 font-medium">{item.quantity_billed}</td>
                                                <td className="px-6 py-4 text-center text-gray-500 text-xs">x {item.conversion_factor}</td>
                                                <td className="px-6 py-4 text-right font-bold text-blue-600 bg-blue-50/30">{item.quantity_received}</td>
                                                <td className="px-6 py-4 text-right text-gray-600">{formatCurrency(item.unit_cost)}</td>
                                                <td className="px-6 py-4 text-right font-bold text-gray-800 bg-gray-50/50">{formatCurrency(item.total_price)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="px-6 py-10 text-center text-gray-500">
                                                Nenhum item encontrado nesta nota fiscal.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============================================================================
// TELA PRINCIPAL DE ENTRADA DE NF E MANUAL
// ============================================================================
const InvoiceEntryPage = ({ formatCurrency }) => {
    const { apiFetch } = useApi();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const [deposits, setDeposits] = useState([]);
    const [products, setProducts] = useState([]);
    
    // Catálogo de produtos/embalagens específicos do Fornecedor (Entrada Manual)
    const [supplierCatalog, setSupplierCatalog] = useState(null);

    const [orders, setOrders] = useState([]);
    const [orderItems, setOrderItems] = useState([]); 
    
    const [invoicesHistory, setInvoicesHistory] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const [parsedData, setParsedData] = useState(null);
    const [selectedDeposit, setSelectedDeposit] = useState('');
    const [selectedOrder, setSelectedOrder] = useState('');
    const [items, setItems] = useState([]);

    const [isFinanceModalOpen, setIsFinanceModalOpen] = useState(false);
    const [financeInitialData, setFinanceInitialData] = useState(null);

    const [viewingInvoice, setViewingInvoice] = useState(null);
    const [invoiceToPrint, setInvoiceToPrint] = useState(null);
    
    const fileInputRef = useRef(null);

    useEffect(() => {
        const handleAfterPrint = () => setInvoiceToPrint(null);
        window.addEventListener('afterprint', handleAfterPrint);
        return () => window.removeEventListener('afterprint', handleAfterPrint);
    }, []);

    const fetchInvoicesHistory = useCallback(async () => {
        try {
            const data = await apiFetch('/invoice-entry/list');
            setInvoicesHistory(data || []);
        } catch (err) {
            console.error("Erro ao buscar histórico de notas:", err);
        }
    }, [apiFetch]);

    useEffect(() => {
        const fetchAuxData = async () => {
            try {
                const [depsData, prodsData] = await Promise.all([
                    apiFetch('/invoice-entry/deposits'),
                    apiFetch('/invoice-entry/products')
                ]);
                setDeposits(depsData || []);
                setProducts(prodsData || []);
            } catch (err) {
                console.error("Erro ao buscar dados auxiliares:", err);
            }
        };
        fetchAuxData();
        fetchInvoicesHistory();
    }, [apiFetch, fetchInvoicesHistory]);

    // Buscar pedidos e o Catálogo Restrito quando um fornecedor for identificado
    useEffect(() => {
        if (parsedData && parsedData.supplier_id) {
            // Busca Pedidos
            apiFetch(`/invoice-entry/orders/${parsedData.supplier_id}`)
                .then(data => setOrders(data || []))
                .catch(err => console.error("Erro ao buscar pedidos:", err));

            // Busca Catálogo Específico (Apenas para Entrada Manual)
            if (parsedData.isManual) {
                apiFetch(`/invoice-entry/supplier-products/${parsedData.supplier_id}`)
                    .then(data => setSupplierCatalog(data || []))
                    .catch(err => {
                        console.error("Erro ao buscar catálogo do fornecedor:", err);
                        setSupplierCatalog([]);
                    });
            }
        } else {
            setSupplierCatalog(null);
        }
    }, [parsedData, apiFetch]);

    useEffect(() => {
        if (selectedOrder) {
            apiFetch(`/invoice-entry/order-items/${selectedOrder}`)
                .then(data => setOrderItems(data || []))
                .catch(err => console.error("Erro ao buscar itens do pedido:", err));
        } else {
            setOrderItems([]);
        }
    }, [selectedOrder, apiFetch]);

    useEffect(() => {
        if (parsedData?.isManual) {
            const total = items.reduce((acc, item) => acc + (parseFloat(item.total_price) || 0), 0);
            if (parsedData.total_amount !== total) {
                setParsedData(prev => ({ ...prev, total_amount: total }));
            }
        }
    }, [items, parsedData?.isManual, parsedData?.total_amount]);

    const handleManualEntry = () => {
        setError('');
        setSuccess('');
        setParsedData({
            isManual: true,
            access_key: `MANUAL-${Date.now()}`,
            invoice_number: '',
            series: '',
            issue_date: new Date().toISOString().split('T')[0],
            supplier_id: null,
            supplier_name: '',
            supplier_cnpj: '',
            total_amount: 0,
        });
        setItems([]);
        setSelectedDeposit('');
        setSelectedOrder('');
        setSupplierCatalog(null); 
    };

    const handleAddManualItem = () => {
        if (!parsedData.supplier_id) {
            setError("Por favor, selecione um Fornecedor primeiro para carregar os produtos negociados.");
            return;
        }

        setItems([...items, {
            supplier_product_code: 'MANUAL',
            supplier_product_name: '',
            packaging_type: 'UN',
            quantity_billed: 1,
            unit_price_billed: 0,
            total_price: 0,
            system_variation_id: '',
            system_product_id: '',
            conversion_factor: 1,
            mapped: true,
            availablePackagings: [] 
        }]);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.type !== 'text/xml' && !file.name.endsWith('.xml')) {
            setError("Por favor, selecione um arquivo XML válido.");
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        const formData = new FormData();
        formData.append('xmlFile', file);

        try {
            const data = await apiFetch('/invoice-entry/parse-xml', {
                method: 'POST',
                body: formData
            });
            
            setParsedData(data);
            setItems(data.items || []);
            
            if (!data.supplier_id) {
                setError(`Fornecedor não encontrado no sistema (CNPJ: ${data.supplier_cnpj}). Cadastre-o primeiro.`);
            }

        } catch (err) {
            setError(err.message || "Erro ao processar o XML.");
        } finally {
            setLoading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...items];
        const item = newItems[index];

        if (parsedData.isManual && field === 'system_variation_id') {
            const selectedProduct = value; 
            if (selectedProduct) {
                item.system_variation_id = selectedProduct.variation_id;
                item.system_product_id = selectedProduct.product_id;
                item.supplier_product_name = selectedProduct.product_name;
                item.supplier_product_code = selectedProduct.supplier_product_code || 'MANUAL';
                item.availablePackagings = selectedProduct.packagings || [];
                
                if (item.availablePackagings.length > 0) {
                    item.packaging_type = item.availablePackagings[0].packaging_type;
                    item.conversion_factor = item.availablePackagings[0].conversion_factor;
                }
                
                if (item.unit_price_billed === 0 && selectedProduct.current_cost) {
                     item.unit_price_billed = (selectedProduct.current_cost * item.conversion_factor).toFixed(2);
                }
            } else {
                item.system_variation_id = '';
                item.system_product_id = '';
                item.supplier_product_name = '';
                item.availablePackagings = [];
            }
        } else {
            item[field] = value;
        }

        if (!parsedData.isManual && field === 'system_variation_id') {
            const selectedProd = products.find(p => String(p.variation_id) === String(value));
            if (selectedProd) {
                item.system_product_id = selectedProd.product_id;
            } else {
                item.system_product_id = '';
            }
        }

        if (parsedData.isManual && field === 'packaging_type') {
             const selectedPkg = item.availablePackagings.find(p => p.packaging_type === value);
             if (selectedPkg) {
                 item.conversion_factor = selectedPkg.conversion_factor;
                 
                 if (item.system_variation_id && supplierCatalog) {
                     const catProd = supplierCatalog.find(p => String(p.variation_id) === String(item.system_variation_id));
                     if (catProd && catProd.current_cost) {
                          item.unit_price_billed = (catProd.current_cost * item.conversion_factor).toFixed(2);
                     }
                 }
             }
        }

        if (parsedData.isManual && (field === 'quantity_billed' || field === 'unit_price_billed' || field === 'system_variation_id' || field === 'packaging_type')) {
            const qty = parseFloat(item.quantity_billed) || 0;
            const unit = parseFloat(item.unit_price_billed) || 0;
            item.total_price = qty * unit;
        }

        if (!parsedData.isManual && (field === 'system_variation_id' || field === 'packaging_type')) {
            const pkgType = field === 'packaging_type' ? value : item.packaging_type;
            if (parsedData.supplier_packagings && item.system_product_id && pkgType) {
                const pkgMatch = parsedData.supplier_packagings.find(
                    p => String(p.product_id) === String(item.system_product_id) && 
                         p.packaging_type.toUpperCase() === pkgType.toUpperCase()
                );
                if (pkgMatch) {
                    item.conversion_factor = pkgMatch.conversion_factor;
                }
            }
        }
        setItems(newItems);
    };

    const handleCancel = () => {
        setParsedData(null);
        setItems([]);
        setSelectedDeposit('');
        setSelectedOrder('');
        setOrders([]);
        setOrderItems([]);
        setSupplierCatalog(null);
        setError('');
        setSuccess('');
    };

    const handleFinanceSuccess = () => {
        setIsFinanceModalOpen(false);
        setFinanceInitialData(null);
        setSuccess("Nota Fiscal e Fatura lançadas com sucesso!");
        fetchInvoicesHistory();
        setTimeout(() => handleCancel(), 2000);
    };

    const handleFinanceCancel = () => {
        setIsFinanceModalOpen(false);
        setFinanceInitialData(null);
        setSuccess("Nota Fiscal processada. (Lançamento financeiro ignorado)");
        fetchInvoicesHistory();
        setTimeout(() => handleCancel(), 2000);
    }

    const handleProcessInvoice = async () => {
        setError('');
        setSuccess('');

        if (!parsedData.supplier_id) {
            setError("Não é possível processar: Fornecedor não informado/cadastrado.");
            return;
        }
        if (!selectedDeposit) {
            setError("Por favor, selecione o Depósito de destino.");
            return;
        }

        const unmapped = items.filter(i => !i.system_variation_id);
        if (unmapped.length > 0) {
            const proceed = window.confirm(
                parsedData.isManual 
                ? `Existem ${unmapped.length} linhas vazias ou sem produto vinculado. Elas serão IGNORADAS. Deseja continuar?`
                : `Existem ${unmapped.length} itens não vinculados a variações do sistema. Eles serão IGNORADOS no estoque. Deseja continuar?`
            );
            if (!proceed) return;
        }

        const validItems = items.filter(i => i.system_variation_id);
        if (validItems.length === 0) {
             setError("Você não adicionou nenhum item válido para dar entrada no estoque.");
             return;
        }

        let closeOrder = true; 

        if (selectedOrder && orderItems.length > 0) {
            const selectedOrderData = orders.find(o => String(o.id) === String(selectedOrder));
            const orderTotal = selectedOrderData ? selectedOrderData.total_amount : 0;

            let priceWarnings = [];
            let qtyWarnings = [];
            let isPartial = false;

            const invGrouped = items.reduce((acc, item) => {
                const key = item.system_variation_id || item.system_product_id;
                if (!key) return acc;
                
                if (!acc[key]) {
                    acc[key] = { 
                        qty: 0, 
                        unit_price: parseFloat(item.unit_price_billed || 0), 
                        name: item.supplier_product_name || 'Produto Manual',
                        product_id: item.system_product_id,
                        variation_id: item.system_variation_id
                    };
                }
                acc[key].qty += parseFloat(item.quantity_billed || 0);
                return acc;
            }, {});

            for (const oItem of orderItems) {
                const invItem = invGrouped[oItem.product_id] || Object.values(invGrouped).find(i => String(i.product_id) === String(oItem.product_id));
                
                if (invItem) {
                    if (invItem.unit_price > oItem.unit_price && Math.abs(invItem.unit_price - oItem.unit_price) > 0.05) {
                        priceWarnings.push(`- ITEM: ${invItem.name}\n  Negociado: ${formatCurrency(oItem.unit_price)}\n  Cobrado na NF: ${formatCurrency(invItem.unit_price)}`);
                    }
                    if (invItem.qty < oItem.quantity) {
                        qtyWarnings.push(`- ${invItem.name}: Pedido (${oItem.quantity}) vs Nota (${invItem.qty})`);
                        isPartial = true;
                    }
                } else {
                    const prodName = products.find(p => String(p.variation_id) === String(oItem.product_id) || String(p.product_id) === String(oItem.product_id))?.name || `Produto ID ${oItem.product_id}`;
                    qtyWarnings.push(`- Faltando na Nota: ${prodName} (Pediu: ${oItem.quantity})`);
                    isPartial = true;
                }
            }

            if (parsedData.total_amount > orderTotal && Math.abs(parsedData.total_amount - orderTotal) > 0.05) {
                priceWarnings.push(`- TOTAL GERAL:\n  Negociado: ${formatCurrency(orderTotal)}\n  Cobrado na NF: ${formatCurrency(parsedData.total_amount)}`);
            }

            if (priceWarnings.length > 0) {
                const proceed = window.confirm(`ATENÇÃO: A Nota Fiscal possui valores ACIMA do negociado no Pedido!\n\n${priceWarnings.join('\n\n')}\n\nDeseja confirmar a entrada mesmo com esses valores mais altos?`);
                if (!proceed) return;
            }

            if (isPartial) {
                closeOrder = window.confirm(`ATENÇÃO: A quantidade de itens na nota é INFERIOR ao que foi pedido.\n\n${qtyWarnings.join('\n')}\n\nDeseja ENCERRAR o pedido de compra mesmo faltando itens?\n\n(Clique em OK para Encerrar, ou Cancelar para marcá-lo como "Atendido Parcialmente")`);
            }
        }

        setLoading(true);
        try {
            const payload = {
                access_key: parsedData.access_key,
                invoice_number: parsedData.invoice_number || 'S/N', 
                series: parsedData.series,
                issue_date: parsedData.issue_date,
                supplier_id: parsedData.supplier_id,
                supplier_cnpj: parsedData.supplier_cnpj,
                total_amount: parsedData.total_amount,
                linked_order_id: selectedOrder || null,
                close_order: closeOrder, 
                target_deposit_id: selectedDeposit,
                items: validItems 
            };

            await apiFetch('/invoice-entry', {
                method: 'POST',
                body: payload
            });

            const querLancar = window.confirm("Recebimento salvo com sucesso e Estoque atualizado!\n\nDeseja lançar este documento no Contas a Pagar agora?");
            
            if (querLancar) {
                setFinanceInitialData({
                    fornecedor: { id: parsedData.supplier_id, name: parsedData.supplier_name_db || parsedData.supplier_name },
                    nf_numero: parsedData.invoice_number,
                    data_emissao: parsedData.issue_date,
                    valor_total: parsedData.total_amount
                });
                setIsFinanceModalOpen(true);
            } else {
                setSuccess("Entrada processada com sucesso no estoque!");
                fetchInvoicesHistory(); 
                setTimeout(() => handleCancel(), 2000);
            }

        } catch (err) {
            setError(err.message || "Erro ao salvar a entrada.");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteInvoice = async (invoiceId, invoiceNumber, linkedOrderId) => {
        if (!window.confirm(`ATENÇÃO: Tem certeza que deseja excluir a Entrada da NF-e nº ${invoiceNumber}?`)) {
            return;
        }

        let reopenOrder = false;
        if (linkedOrderId) {
            reopenOrder = window.confirm(`Esta nota está vinculada a um Pedido de Compra.\n\nDeseja REABRIR o Pedido de Compra (voltar para 'Aguardando Chegada')?\n\nClique em OK para Reabrir o Pedido, ou Cancelar para mantê-lo Atendido.`);
        }
        
        setError('');
        setSuccess('');
        setLoading(true);
        
        try {
            const response = await apiFetch(`/invoice-entry/${invoiceId}?reopenOrder=${reopenOrder}`, { method: 'DELETE' });
            setSuccess(response.message || 'Nota fiscal excluída com sucesso.');
            fetchInvoicesHistory(); 
        } catch (err) {
            setError(err.message || "Erro ao excluir a Nota Fiscal.");
        } finally {
            setLoading(false);
        }
    };

    const handleViewInvoice = async (invoiceId) => {
        try {
            const data = await apiFetch(`/invoice-entry/${invoiceId}`);
            setViewingInvoice(data);
        } catch (err) {
            setError("Erro ao carregar detalhes da nota.");
        }
    };

    const handlePrintInvoice = async (invoiceId) => {
        try {
            const data = await apiFetch(`/invoice-entry/${invoiceId}`);
            setInvoiceToPrint(data);
            setTimeout(() => {
                window.print();
            }, 500);
        } catch (err) {
            setError("Erro ao carregar os dados para impressão.");
        }
    };

    const filteredInvoices = invoicesHistory.filter(inv => {
        if (!searchQuery || !searchQuery.trim()) return false;
        
        const query = searchQuery.toLowerCase().trim();
        const cleanQuery = query.replace(/\D/g, ''); // Limpa formatação (pontos, traços) para CNPJ
        
        const invoiceStr = inv.invoice_number ? String(inv.invoice_number).toLowerCase() : '';
        const supplierStr = inv.supplier_name ? String(inv.supplier_name).toLowerCase() : '';
        const cnpjStr = inv.supplier_cnpj ? String(inv.supplier_cnpj).toLowerCase() : '';
        const cleanCnpj = cnpjStr.replace(/\D/g, ''); // Limpa formatação do banco
        
        return invoiceStr.includes(query) || 
               supplierStr.includes(query) || 
               (cnpjStr && cnpjStr.includes(query)) ||
               (cleanQuery && cleanCnpj && cleanCnpj.includes(cleanQuery));
    });

    const totalPages = Math.ceil(filteredInvoices.length / ITEMS_PER_PAGE) || 1;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const currentInvoices = filteredInvoices.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    return (
        <>
            <div className="p-2 md:p-6 font-inter max-w-7xl mx-auto relative print:hidden">
                <div className="mb-6">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 flex items-center gap-3">
                        <FileText className="text-blue-600" /> Consulta e Entrada de NF
                    </h1>
                    <p className="text-gray-600 mt-1">Consulte o histórico de notas recebidas ou importe novos XMLs.</p>
                </div>

                {error && <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded shadow-sm">{error}</div>}
                {success && <div className="mb-4 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 rounded shadow-sm">{success}</div>}

                {isFinanceModalOpen && financeInitialData && (
                    <div className="absolute inset-0 z-50">
                        <NovaFaturaModal 
                            initialData={financeInitialData}
                            onClose={handleFinanceCancel}
                            onSave={handleFinanceSuccess}
                            formatCurrency={formatCurrency}
                        />
                    </div>
                )}

                {viewingInvoice && (
                    <VisualizarNotaModal 
                        invoice={viewingInvoice} 
                        onClose={() => setViewingInvoice(null)} 
                        formatCurrency={formatCurrency}
                    />
                )}

                {/* TELA INICIAL: UPLOAD + LISTAGEM */}
                {!parsedData && (
                    <div className="space-y-6 animate-in fade-in">
                        
                        {/* CARD COMPACTO DE UPLOAD E ENTRADA MANUAL */}
                        <div className="bg-white p-5 md:p-6 rounded-xl shadow-sm border border-gray-200 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4 text-center md:text-left">
                                <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center shrink-0 mx-auto md:mx-0">
                                    <UploadCloud size={28} className="text-blue-600" />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-gray-800">Nova Entrada de NF-e</h2>
                                    <p className="text-sm text-gray-500">Selecione o arquivo .xml para alimentar o estoque automaticamente.</p>
                                </div>
                            </div>
                            
                            <div className="flex flex-col gap-2 w-full md:w-auto items-center md:items-stretch">
                                <input 
                                    type="file" 
                                    accept=".xml, text/xml" 
                                    onChange={handleFileUpload} 
                                    className="hidden" 
                                    ref={fileInputRef}
                                />
                                <button 
                                    onClick={() => fileInputRef.current.click()} 
                                    disabled={loading}
                                    className="w-full px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 shadow-md transition-all flex items-center justify-center gap-2 disabled:bg-blue-400"
                                >
                                    {loading ? <Loader2 className="animate-spin" /> : <FileText size={18} />}
                                    {loading ? 'Processando XML...' : 'Importar Arquivo XML'}
                                </button>
                                
                                <button 
                                    onClick={handleManualEntry}
                                    disabled={loading}
                                    className="text-xs text-blue-600 font-semibold hover:text-blue-800 transition-colors underline disabled:opacity-50 mt-1"
                                >
                                    Ou clique aqui para Entrada Manual
                                </button>
                            </div>
                        </div>

                        {/* PAINEL DE CONSULTA (HISTÓRICO) */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-4 border-b flex flex-col md:flex-row items-center justify-between gap-4 bg-gray-50">
                                <h3 className="font-bold text-gray-800">Notas Fiscais Lançadas</h3>
                                
                                <div className="flex w-full md:w-auto gap-2">
                                    <div className="relative flex-1 md:w-72">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                                        <input 
                                            type="text" 
                                            placeholder="Buscar por NF, Fornecedor ou CNPJ/CPF..." 
                                            value={searchQuery}
                                            onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                                        />
                                    </div>
                                    <button 
                                        onClick={() => { fetchInvoicesHistory(); setCurrentPage(1); }}
                                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center justify-center"
                                        title="Localizar"
                                    >
                                        <Search size={20} />
                                    </button>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm text-left whitespace-nowrap">
                                    <thead className="bg-white text-gray-500 font-semibold uppercase text-[10px] tracking-wider border-b">
                                        <tr>
                                            <th className="px-4 py-3">Nº Documento</th>
                                            <th className="px-4 py-3">Fornecedor</th>
                                            <th className="px-4 py-3">Emissão</th>
                                            <th className="px-4 py-3">Depósito Recebedor</th>
                                            <th className="px-4 py-3">Valor Total</th>
                                            <th className="px-4 py-3 text-center">Status e Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {currentInvoices.length === 0 ? (
                                            <tr>
                                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                    {!searchQuery.trim() 
                                                        ? "Utilize a barra de pesquisa para localizar notas fiscais lançadas." 
                                                        : "Nenhuma nota fiscal encontrada para esta pesquisa."}
                                                </td>
                                            </tr>
                                        ) : currentInvoices.map((inv) => (
                                            <tr key={inv.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-4 py-3 font-bold text-gray-800">{inv.invoice_number === 'S/N' ? 'Entrada Manual' : `NF-e ${inv.invoice_number}`}</td>
                                                <td className="px-4 py-3 text-gray-600 truncate max-w-[200px]" title={inv.supplier_name}>{inv.supplier_name || 'Desconhecido'}</td>
                                                <td className="px-4 py-3 text-gray-600">{inv.issue_date ? inv.issue_date.split('-').reverse().join('/') : '-'}</td>
                                                <td className="px-4 py-3 text-gray-600">{inv.deposit_name || '-'}</td>
                                                <td className="px-4 py-3 font-bold text-gray-800">{formatCurrency(inv.total_amount)}</td>
                                                <td className="px-4 py-3 text-center">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-700 mr-2">
                                                            <Check size={12}/> {inv.status}
                                                        </span>
                                                        
                                                        <button 
                                                            onClick={() => handleViewInvoice(inv.id)}
                                                            className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                                            title="Visualizar Lançamento"
                                                        >
                                                            <Eye size={18} />
                                                        </button>
                                                        
                                                        <button 
                                                            onClick={() => handlePrintInvoice(inv.id)}
                                                            className="p-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                                            title="Imprimir Espelho"
                                                        >
                                                            <Printer size={18} />
                                                        </button>

                                                        <button 
                                                            onClick={() => handleDeleteInvoice(inv.id, inv.invoice_number, inv.linked_order_id)}
                                                            disabled={loading}
                                                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                                                            title="Excluir Entrada"
                                                        >
                                                            <Trash2 size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="p-4 border-t flex items-center justify-between bg-white">
                                    <span className="text-sm text-gray-500">
                                        Página <span className="font-bold">{currentPage}</span> de <span className="font-bold">{totalPages}</span>
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                            className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <button 
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                            className="p-1.5 border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* TELA 2: CONFERÊNCIA (XML) OU ENTRADA MANUAL */}
                {parsedData && (
                    <div className="space-y-6 animate-in slide-in-from-bottom-4 fade-in">
                        
                        {/* CABEÇALHO CONDICIONAL (MANUAL OU XML) */}
                        {parsedData.isManual ? (
                            <div className="bg-blue-50/30 p-5 rounded-xl shadow-sm border border-blue-200 grid grid-cols-1 md:grid-cols-12 gap-4 items-center">
                                <div className="col-span-1 md:col-span-5">
                                    <label className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">Fornecedor*</label>
                                    <FornecedorSearchInput 
                                        onFornecedorSelect={(f) => {
                                            setParsedData(prev => ({
                                                ...prev, 
                                                supplier_id: f ? f.id : null,
                                                supplier_name: f ? f.name : '',
                                                supplier_cnpj: f ? f.document_id : ''
                                            }));
                                            if (!f) {
                                                setItems([]); 
                                            }
                                        }}
                                    />
                                    {parsedData.supplier_id && !supplierCatalog && (
                                        <p className="text-[10px] text-blue-500 mt-1 flex items-center gap-1">
                                            <Loader2 size={10} className="animate-spin"/> Carregando catálogo...
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-1 md:col-span-3 flex gap-2">
                                    <div className="flex-1">
                                        <label className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">Documento (NF)</label>
                                        <input 
                                            type="text" 
                                            value={parsedData.invoice_number} 
                                            onChange={e => setParsedData({...parsedData, invoice_number: e.target.value})} 
                                            className="w-full p-2 border border-gray-300 rounded-md text-sm shadow-sm focus:ring-blue-500" 
                                            placeholder="Opcional" 
                                        />
                                    </div>
                                </div>
                                <div className="col-span-1 md:col-span-2">
                                    <label className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1 block">Emissão*</label>
                                    <input 
                                        type="date" 
                                        value={parsedData.issue_date} 
                                        onChange={e => setParsedData({...parsedData, issue_date: e.target.value})} 
                                        className="w-full p-2 border border-gray-300 rounded-md text-sm shadow-sm focus:ring-blue-500" 
                                        required 
                                    />
                                </div>
                                <div className="col-span-1 md:col-span-2 text-left md:text-right">
                                    <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-1">Valor Total</p>
                                    <p className="text-2xl font-black text-blue-700">{formatCurrency(parsedData.total_amount)}</p>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                <div className="col-span-1 md:col-span-2 flex items-center gap-4">
                                    <div className={`p-3 rounded-full ${parsedData.supplier_id ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                        {parsedData.supplier_id ? <CheckCircle size={24} /> : <AlertTriangle size={24} />}
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Fornecedor</p>
                                        <p className="font-bold text-gray-900 truncate">{parsedData.supplier_name}</p>
                                        <p className="text-sm text-gray-500">CNPJ: {parsedData.supplier_cnpj}</p>
                                    </div>
                                </div>
                                <div>
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Documento</p>
                                    <p className="font-bold text-gray-900">NF-e nº {parsedData.invoice_number}</p>
                                    <p className="text-sm text-gray-500">Emissão: {parsedData.issue_date.split('-').reverse().join('/')}</p>
                                </div>
                                <div className="text-left md:text-right border-t md:border-t-0 pt-3 md:pt-0">
                                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Valor Total</p>
                                    <p className="text-2xl font-black text-blue-700">{formatCurrency(parsedData.total_amount)}</p>
                                </div>
                            </div>
                        )}

                        <div className="bg-blue-50/50 p-5 rounded-xl border border-blue-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                    <Warehouse size={16} className="text-blue-600" /> Depósito de Destino (Obrigatório)
                                </label>
                                <select 
                                    value={selectedDeposit} 
                                    onChange={(e) => setSelectedDeposit(e.target.value)}
                                    className="w-full p-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="">Selecione o depósito que receberá os itens...</option>
                                    {deposits.map(d => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2 flex items-center gap-2">
                                    <FileText size={16} className="text-blue-600" /> Pedido de Compra Vinculado
                                </label>
                                <select 
                                    value={selectedOrder} 
                                    onChange={(e) => setSelectedOrder(e.target.value)}
                                    disabled={orders.length === 0}
                                    className="w-full p-2.5 bg-white border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                >
                                    <option value="">Nenhum pedido vinculado (Entrada Avulsa)</option>
                                    {orders.map(o => (
                                        <option key={o.id} value={o.id}>
                                            Pedido #{o.id} - Previsto: {o.expected_delivery_date ? o.expected_delivery_date.split('-').reverse().join('/') : 'N/A'} - {formatCurrency(o.total_amount)}
                                        </option>
                                    ))}
                                </select>
                                {orders.length === 0 && parsedData.supplier_id && (
                                    <p className="text-xs text-orange-600 mt-1 font-medium">Não há pedidos pendentes para este fornecedor.</p>
                                )}
                            </div>
                        </div>

                        {/* ITENS CONDICIONAL (MANUAL OU XML) */}
                        {parsedData.isManual ? (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <Package className="text-gray-600" size={18}/> Itens da Entrada Manual
                                    </h3>
                                    <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-1 rounded-full">{items.length} Itens</span>
                                </div>
                                <div className="overflow-x-auto min-h-[250px]">
                                    <table className="w-full text-sm text-left whitespace-nowrap">
                                        <thead className="bg-gray-100/50 text-gray-600 font-semibold uppercase text-[10px] tracking-wider border-b">
                                            <tr>
                                                <th className="px-4 py-3 min-w-[300px]">Produto (Sistema)</th>
                                                <th className="px-4 py-3 text-center w-32">Embalagem</th>
                                                <th className="px-4 py-3 text-center w-24">Qtd. Un</th>
                                                <th className="px-4 py-3 text-right w-32">Custo Un.</th>
                                                <th className="px-4 py-3 text-right w-32">Subtotal</th>
                                                <th className="px-4 py-3 text-center w-16">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {items.map((item, index) => {
                                                const finalQty = (item.quantity_billed * parseFloat(item.conversion_factor || 1)).toFixed(2);
                                                return (
                                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3">
                                                            <ProdutoFornecedorSearchInput 
                                                                catalog={supplierCatalog}
                                                                selectedVariationId={item.system_variation_id}
                                                                onProductSelect={(prod) => handleItemChange(index, 'system_variation_id', prod)}
                                                            />
                                                            {item.system_variation_id && item.conversion_factor !== 1 && (
                                                                <p className="text-[10px] text-gray-500 mt-1">
                                                                    Entrará no estoque: <b className="text-blue-600">{finalQty} un.</b> (Fator: {item.conversion_factor})
                                                                </p>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <select
                                                                value={item.packaging_type}
                                                                onChange={(e) => handleItemChange(index, 'packaging_type', e.target.value)}
                                                                disabled={!item.system_variation_id || item.availablePackagings?.length === 0}
                                                                className="w-full p-2 border border-gray-300 rounded-md text-center text-sm shadow-sm focus:ring-2 focus:ring-blue-500 uppercase bg-white cursor-pointer disabled:bg-gray-100 disabled:cursor-not-allowed"
                                                            >
                                                                {item.availablePackagings?.map(pkg => (
                                                                    <option key={pkg.packaging_type} value={pkg.packaging_type}>
                                                                        {pkg.packaging_type.toUpperCase()} (x{pkg.conversion_factor})
                                                                    </option>
                                                                ))}
                                                                {(!item.availablePackagings || item.availablePackagings.length === 0) && (
                                                                    <option value="UN">UN (x1)</option>
                                                                )}
                                                            </select>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <input 
                                                                type="number" min="0.01" step="any"
                                                                value={item.quantity_billed || ''}
                                                                onChange={(e) => handleItemChange(index, 'quantity_billed', e.target.value)}
                                                                className="w-full p-2 border border-gray-300 rounded-md text-center text-sm shadow-sm focus:ring-2 focus:ring-blue-500 bg-white"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right">
                                                            <input 
                                                                type="number" min="0.00" step="any"
                                                                value={item.unit_price_billed || ''}
                                                                onChange={(e) => handleItemChange(index, 'unit_price_billed', e.target.value)}
                                                                className="w-full p-2 border border-gray-300 rounded-md text-right text-sm shadow-sm focus:ring-2 focus:ring-blue-500 bg-white"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-right font-bold text-gray-800">
                                                            {formatCurrency(item.total_price || 0)}
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <button 
                                                                onClick={() => {
                                                                    const newItems = items.filter((_, i) => i !== index);
                                                                    setItems(newItems);
                                                                }} 
                                                                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                                                                title="Remover Item"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            {items.length === 0 && (
                                                <tr>
                                                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                                        Nenhum produto adicionado. Clique no botão abaixo para iniciar.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="p-4 bg-gray-50 border-t flex justify-center">
                                    <button 
                                        onClick={handleAddManualItem}
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 font-semibold rounded-lg hover:bg-blue-200 transition-colors shadow-sm"
                                    >
                                        <PlusCircle size={18} /> Adicionar Produto
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                                <div className="p-4 border-b bg-gray-50 flex items-center justify-between">
                                    <h3 className="font-bold text-gray-800 flex items-center gap-2">
                                        <Package className="text-gray-600" size={18}/> Mapeamento de Produtos (De - Para)
                                    </h3>
                                    <span className="text-xs font-bold text-gray-500 bg-gray-200 px-2 py-1 rounded-full">{items.length} Itens na Nota</span>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm text-left whitespace-nowrap">
                                        <thead className="bg-gray-100/50 text-gray-600 font-semibold uppercase text-[10px] tracking-wider border-b">
                                            <tr>
                                                <th className="px-4 py-3 border-r">Dados do Fornecedor (XML)</th>
                                                <th className="px-4 py-3 bg-blue-50/30">Variação Correspondente (Sistema)</th>
                                                <th className="px-4 py-3 bg-blue-50/30 text-center w-28">Embalagem</th>
                                                <th className="px-4 py-3 bg-blue-50/30 text-center w-24">Fator</th>
                                                <th className="px-4 py-3 bg-blue-50/30 text-right w-32">Conversão & Custo Real</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {items.map((item, index) => {
                                                const finalQty = (item.quantity_billed * parseFloat(item.conversion_factor || 1)).toFixed(2);
                                                const finalCost = (item.total_price / (finalQty > 0 ? finalQty : 1));

                                                return (
                                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                                        <td className="px-4 py-3 border-r bg-gray-50/30">
                                                            <div className="font-medium text-gray-900 truncate max-w-[200px] md:max-w-[250px]" title={item.supplier_product_name}>
                                                                {item.supplier_product_name}
                                                            </div>
                                                            <div className="flex gap-4 mt-1 text-xs text-gray-500 font-medium">
                                                                <span>Cód: {item.supplier_product_code}</span>
                                                                <span>Qtd: <b className="text-gray-800">{item.quantity_billed}</b></span>
                                                                <span>Total: <b className="text-gray-800">{formatCurrency(item.total_price)}</b></span>
                                                            </div>
                                                        </td>

                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2">
                                                                {item.mapped && <span className="flex items-center gap-1 text-[10px] font-bold text-green-600 bg-green-100 px-1.5 py-0.5 rounded" title="Mapeamento salvo anteriormente"><CheckCircle size={12}/> Memória</span>}
                                                                <select 
                                                                    value={item.system_variation_id}
                                                                    onChange={(e) => handleItemChange(index, 'system_variation_id', e.target.value)}
                                                                    className={`w-full p-2 border rounded-md text-sm shadow-sm focus:ring-2 focus:ring-blue-500 ${item.system_variation_id ? 'border-gray-300 bg-white' : 'border-orange-300 bg-orange-50'}`}
                                                                >
                                                                    <option value="">-- Vincule à variação do produto --</option>
                                                                    {products.map(p => (
                                                                        <option key={p.variation_id} value={p.variation_id}>{p.name}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                        </td>
                                                        
                                                        <td className="px-4 py-3 text-center">
                                                            <select
                                                                value={item.packaging_type}
                                                                onChange={(e) => handleItemChange(index, 'packaging_type', e.target.value)}
                                                                className="w-24 p-2 border border-gray-300 rounded-md text-center text-sm shadow-sm focus:ring-2 focus:ring-blue-500 uppercase bg-white cursor-pointer"
                                                            >
                                                                {!['UN', 'CX', 'KG', 'FD', 'SCH', 'SC', 'DP', 'EB', 'ML'].includes(item.packaging_type?.toUpperCase()) && item.packaging_type && (
                                                                    <option value={item.packaging_type}>{item.packaging_type.toUpperCase()}</option>
                                                                )}
                                                                <option value="UN">UN</option>
                                                                <option value="CX">CX</option>
                                                                <option value="KG">KG</option>
                                                                <option value="FD">FD</option>
                                                                <option value="SCH">SCH</option>
                                                                <option value="SC">SC</option>
                                                                <option value="DP">DP</option>
                                                                <option value="EB">EB</option>
                                                                <option value="ML">ML</option>
                                                            </select>
                                                        </td>

                                                        <td className="px-4 py-3 text-center">
                                                            <input 
                                                                type="number" 
                                                                min="0.01" step="any"
                                                                value={item.conversion_factor}
                                                                onChange={(e) => handleItemChange(index, 'conversion_factor', e.target.value)}
                                                                className="w-20 p-2 border border-gray-300 rounded-md text-center text-sm shadow-sm focus:ring-2 focus:ring-blue-500 bg-white"
                                                            />
                                                        </td>

                                                        <td className="px-4 py-3 text-right">
                                                            <div className="flex flex-col items-end">
                                                                <span className="text-xs font-bold text-gray-500">Entrará no estoque:</span>
                                                                <span className="text-sm font-black text-blue-700 bg-blue-50 px-2 py-0.5 rounded mt-0.5">{finalQty} un.</span>
                                                                <span className="text-[10px] text-gray-500 mt-1">Custo Real Un: <b className={finalCost > 0 ? "text-gray-800" : "text-red-500"}>{formatCurrency(finalCost)}</b></span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 pt-2">
                            <button 
                                onClick={handleCancel}
                                disabled={loading}
                                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 shadow-sm transition-all"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={handleProcessInvoice}
                                disabled={loading || !parsedData.supplier_id || items.length === 0}
                                className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 shadow-md transition-all disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} 
                                Processar {parsedData.isManual ? 'Entrada Manual' : 'Entrada de NF'}
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* COMPONENTE DE IMPRESSÃO (OCULTO NA TELA, SOBREPÕE TUDO NA IMPRESSÃO GRAÇAS AO CSS) */}
            {invoiceToPrint && (
                <>
                    <style type="text/css" media="print">
                        {`
                            body * { visibility: hidden; }
                            #print-invoice-section, #print-invoice-section * { visibility: visible; }
                            #print-invoice-section { position: absolute; left: 0; top: 0; width: 100%; margin: 0; padding: 0; }
                        `}
                    </style>

                    <div id="print-invoice-section" className="hidden print:block w-full bg-white text-black p-8 font-sans">
                        <div className="text-center border-b-2 border-gray-800 pb-4 mb-6">
                            <h1 className="text-2xl font-bold uppercase tracking-wider">Espelho de Lançamento - Entrada de Estoque</h1>
                        </div>

                        <div className="mb-6">
                            <h2 className="text-lg font-bold border-b border-gray-300 mb-2 pb-1">Dados Gerais do Recebimento</h2>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="mb-1"><strong>Fornecedor:</strong> {invoiceToPrint.supplier_name}</p>
                                    <p><strong>CNPJ:</strong> {invoiceToPrint.supplier_cnpj || '-'}</p>
                                </div>
                                <div>
                                    <p className="mb-1"><strong>Nota Fiscal / Doc:</strong> Nº {invoiceToPrint.invoice_number === 'S/N' ? 'Entrada Manual' : invoiceToPrint.invoice_number} {invoiceToPrint.series ? `(Série: ${invoiceToPrint.series})` : ''}</p>
                                    <p className="mb-1"><strong>Data do Documento:</strong> {invoiceToPrint.issue_date ? invoiceToPrint.issue_date.split('-').reverse().join('/') : '-'}</p>
                                    <p><strong>Data do Lançamento:</strong> {invoiceToPrint.created_at ? new Date(invoiceToPrint.created_at).toLocaleDateString('pt-BR') : '-'}</p>
                                </div>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                                <p><strong>Depósito de Destino:</strong> {invoiceToPrint.deposit_name || '-'}</p>
                                <p><strong>Valor Total Lançado:</strong> {formatCurrency(invoiceToPrint.total_amount)}</p>
                            </div>
                            {invoiceToPrint.observations && (
                                <div className="mt-4 text-sm">
                                    <p><strong>Observações:</strong> {invoiceToPrint.observations}</p>
                                </div>
                            )}
                        </div>

                        <div>
                            <h2 className="text-lg font-bold border-b border-gray-300 mb-2 pb-1">Itens Lançados no Estoque</h2>
                            <table className="w-full text-sm text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-gray-400">
                                        <th className="py-2">Produto (Sistema)</th>
                                        <th className="py-2 text-right">Qtd. Documento</th>
                                        <th className="py-2 text-center">Fator</th>
                                        <th className="py-2 text-right">Qtd. Estoque</th>
                                        <th className="py-2 text-right">Custo Unit.</th>
                                        <th className="py-2 text-right">Custo Total</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {invoiceToPrint.items && invoiceToPrint.items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td className="py-2 pr-2 truncate max-w-[250px]">{item.product_name}</td>
                                            <td className="py-2 text-right">{item.quantity_billed}</td>
                                            <td className="py-2 text-center">x {item.conversion_factor}</td>
                                            <td className="py-2 text-right font-bold">{item.quantity_received}</td>
                                            <td className="py-2 text-right">{formatCurrency(item.unit_cost)}</td>
                                            <td className="py-2 text-right">{formatCurrency(item.total_price)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-12 text-center text-xs text-gray-500 italic">
                            Documento gerado pelo sistema em {new Date().toLocaleString('pt-BR')}
                        </div>
                    </div>
                </>
            )}
        </>
    );
};

export default InvoiceEntryPage;